import { defaultPropertyDefinitions, propertyDefinitionsByLabel, PropertyTypes, } from "@buildwithflux/constants";
import { isPropertyWithLinkValueType, isPropertyWithLinkValueTypeButStringValue, } from "@buildwithflux/models";
import { intersection, pickBy } from "lodash";
import { DistributorInfo, getSearchUrl } from "../helpers";
/**
 * Check if properties are in the old format.
 *
 * @param properties Properties object to check
 * @returns True if properties are in the old format, false otherwise
 */
export function areOldProperties(properties) {
    const [firstValue] = Object.values(properties);
    if (!firstValue)
        return false;
    return !firstValue.name;
}
/**
 * Get the key for a property (uid for single property, name for mixed/multiple state).
 *
 * @param property Property to get the key for
 * @returns The property uid for regular properties, or name for mixed/multiple state properties
 */
export function getPropertyKey(property) {
    return "state" in property ? property.name : property.uid;
}
/**
 * Implementation of getProperty that handles both signatures.
 */
export function getProperty(propName, properties) {
    if (!properties)
        return undefined;
    if (areOldProperties(properties)) {
        if (propName in properties) {
            return {
                name: propName,
                value: properties[propName],
            };
        }
        return undefined;
    }
    if (propName in properties && properties[propName] && "state" in properties[propName]) {
        return properties[propName];
    }
    return Object.values(properties).find((prop) => {
        if (!prop)
            return false;
        return prop.name === propName;
    });
}
/**
 * Get a property value by name from any property shape.
 *
 * @param propName Name of the property to retrieve value for
 * @param properties Properties object of any supported shape
 * @returns The property value if found, undefined otherwise
 */
export function getPropertyValue(propName, properties) {
    if (!properties)
        return undefined;
    if (areOldProperties(properties)) {
        return properties[propName];
    }
    const property = getProperty(propName, properties);
    if (property && "state" in property && property.state === "mixed") {
        return "";
    }
    return property?.value;
}
/**
 * Normalize any property shape to the standard IPropertiesMap format.
 *
 * @param properties Properties object of any supported shape
 * @returns Normalized properties in IPropertiesMap format
 */
export function normalizeProperties(properties) {
    if (!properties)
        return {};
    if (!areOldProperties(properties) &&
        Object.values(properties).every((p) => p && "uid" in p && "name" in p && "value" in p)) {
        return properties;
    }
    const result = {};
    if (areOldProperties(properties)) {
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        Object.entries(properties).forEach(([key, value]) => {
            result[key] = {
                uid: key,
                name: key,
                value: value,
            };
        });
    }
    else {
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        Object.entries(properties).forEach(([key, prop]) => {
            if (!prop)
                return;
            const property = prop;
            if (!property.name)
                return;
            const uid = "uid" in property ? property.uid : key;
            if (uid) {
                result[uid] = {
                    uid,
                    name: property.name,
                    value: property.value,
                    unit: property.unit,
                    order: property.order,
                    valueType: property.valueType,
                    key: property.key,
                    customAutocompleteOptions: property.customAutocompleteOptions,
                    dynamicAutoComplete: property.dynamicAutoComplete,
                };
            }
        });
    }
    return result;
}
export function getPropertyValueAsString(propertyOrName, properties) {
    if (typeof propertyOrName === "string") {
        const value = getPropertyValue(propertyOrName, properties);
        if (value === undefined || value === null) {
            return undefined;
        }
        return value.toString();
    }
    const property = propertyOrName;
    if (isPropertyWithLinkValueType(property))
        return property.value.url;
    if (isPropertyWithLinkValueTypeButStringValue(property))
        return property.value;
    const value = property.value;
    if (typeof value === "string")
        return value;
    if (typeof value === "object")
        return JSON.stringify(value);
    return "";
}
/**
 * Get a property value as a number.
 *
 * @param propName Name of the property to retrieve value for
 * @param properties Properties object of any supported shape
 * @returns The property value as a number if found and can be converted to a number, undefined otherwise
 */
export function getPropertyValueAsNumber(propName, properties) {
    const value = getPropertyValue(propName, properties);
    if (value === undefined || value === null) {
        return undefined;
    }
    const num = Number(value);
    return isNaN(num) ? undefined : num;
}
/**
 * Get a property value as a boolean.
 *
 * @param propName Name of the property to retrieve value for
 * @param properties Properties object of any supported shape
 * @returns The property value as a boolean if found and can be converted to a boolean, undefined otherwise
 */
export function getPropertyValueAsBoolean(propName, properties) {
    const value = getPropertyValueAsString(propName, properties);
    if (value === undefined) {
        return undefined;
    }
    const trimmed = value.trim().toLowerCase();
    return trimmed === "true" || trimmed === "on" || trimmed === "yes" || trimmed === "1";
}
/**
 * Get a property value with its unit (if available).
 *
 * @param propName Name of the property to retrieve value for
 * @param properties Properties object of any supported shape
 * @returns The property value with its unit as a string if found, undefined otherwise
 */
/**
 * Helper function to get a property value with its unit.
 *
 * @param property Property to get value with unit for
 * @returns The property value with unit as a string if available, undefined otherwise
 */
function getValueWithUnit(property) {
    const value = property?.value;
    const unit = property?.unit;
    if (value && unit) {
        return `${value}${unit}`;
    }
    else if (value) {
        return value.toString();
    }
    return undefined;
}
/**
 * Convert a property value to a string with its unit.
 *
 * @param property Property to convert to string with unit
 * @returns The property value with unit as a string
 */
export function getPropertyValueWithUnitAsString(property) {
    const value = getPropertyValueAsString(property);
    if (value && property.unit) {
        return `${value}${property.unit}`;
    }
    else {
        return value;
    }
}
/**
 * Get a property value with its unit (if available).
 *
 * @param propName Name of the property to retrieve value for
 * @param properties Properties object of any supported shape
 * @returns The property value with its unit as a string if found, undefined otherwise
 */
export function getPropertyValueWithUnit(propName, properties) {
    if (!properties)
        return undefined;
    const property = getProperty(propName, properties);
    return getValueWithUnit(property);
}
/**
 * Get a property by using a default property definition.
 *
 * @param defaultProperty The default property definition
 * @param properties Properties object of any supported shape
 * @returns The property if found, undefined otherwise
 */
export function getPropertyByDefinition(defaultProperty, properties) {
    return getProperty(defaultProperty.label, properties);
}
/**
 * Get a property value by using a default property definition.
 *
 * @param defaultProperty The default property definition
 * @param properties Properties object of any supported shape
 * @returns The property value if found, undefined otherwise
 */
export function getPropertyValueByDefinition(defaultProperty, properties) {
    if (!properties)
        return undefined;
    if (areOldProperties(properties)) {
        return properties[defaultProperty.key];
    }
    return getPropertyValue(defaultProperty.label, properties);
}
/**
 * Get the datasheet property from properties.
 *
 * @param properties Properties object of any supported shape
 * @returns The datasheet property if found, undefined otherwise
 */
export function getDatasheetProperty(properties) {
    if (!properties)
        return undefined;
    return getProperty(defaultPropertyDefinitions.datasheet_url.label, properties);
}
/**
 * Get the datasheet property value as a string.
 *
 * @param properties Properties object
 * @returns The datasheet property value as a string, empty string if not found
 */
export function getDatasheetPropertyValueAsString(properties) {
    const property = getDatasheetProperty(properties);
    if (!property)
        return "";
    return getPropertyValueAsString(property);
}
/**
 * Get the manufacturer property from properties.
 *
 * @param properties Properties object of any supported shape
 * @returns The manufacturer property if found, undefined otherwise
 */
export function getManufacturerProperty(properties) {
    if (!properties)
        return undefined;
    if (areOldProperties(properties)) {
        const manufacturerKey = "Manufacturer";
        const value = properties[manufacturerKey];
        if (value !== undefined) {
            return {
                name: manufacturerKey,
                value,
            };
        }
        return undefined;
    }
    return Object.values(properties).find((prop) => {
        if (!prop)
            return false;
        const propName = prop.name;
        return propName === defaultPropertyDefinitions.manufacturer_name?.label || propName === "Manufacturer";
    });
}
/**
 * Get the manufacturer property value as a string.
 *
 * @param properties Properties object
 * @returns The manufacturer property value as a string, empty string if not found
 */
export function getManufacturerPropertyValueAsString(properties) {
    const property = getManufacturerProperty(properties);
    if (!property)
        return "";
    return getPropertyValueAsString(property);
}
/**
 * Get the manufacturer part number property from properties.
 *
 * @param properties Properties object of any supported shape
 * @returns The manufacturer part number property if found, undefined otherwise
 */
export function getManufacturerPartNumberProperty(properties) {
    if (!properties)
        return undefined;
    return getProperty(defaultPropertyDefinitions.mpn.label, properties);
}
/**
 * Get the manufacturer part number property value as a string.
 *
 * @param properties Properties object
 * @returns The manufacturer part number property value as a string, undefined if not found
 */
export function getManufacturerPartNumberPropertyAsString(properties) {
    const property = getManufacturerPartNumberProperty(properties);
    if (!property)
        return;
    return getPropertyValueAsString(property);
}
/**
 * Get the JLC PCB part class property from properties.
 *
 * @param properties Properties object of any supported shape
 * @returns The JLC PCB part class property if found, undefined otherwise
 */
export function getJlcPcbPartClassProperty(properties) {
    if (!properties)
        return undefined;
    return getProperty(defaultPropertyDefinitions.jlcPcbPartClass.label, properties);
}
/**
 * Get the distributor part number property from properties.
 *
 * @param distributor The distributor to get the part number for
 * @param properties Properties object of any supported shape
 * @returns The distributor part number property if found, undefined otherwise
 */
export function getDistributorPartNumberProperty(distributor, properties) {
    if (!properties)
        return undefined;
    switch (distributor) {
        case "lcsc":
            return getProperty(defaultPropertyDefinitions.lcsc_dpn.label, properties);
        case "digikey":
            return getProperty(defaultPropertyDefinitions.digikey_dpn.label, properties);
        case "mouser":
            return getProperty(defaultPropertyDefinitions.mouser_dpn.label, properties);
        case "arrow":
            return getProperty(defaultPropertyDefinitions.arrow_dpn.label, properties);
        case "verical":
            return getProperty(defaultPropertyDefinitions.verical_dpn.label, properties);
        case "tme":
            return getProperty(defaultPropertyDefinitions.tme_dpn.label, properties);
    }
}
/**
 * Get the distributor product page URL from a distributor part number property.
 *
 * @param propertyName The name of the property
 * @param propertyValue The value of the property
 * @returns The distributor product page URL if found, undefined otherwise
 */
export function getDistributorProductPageFromDpnProperty(propertyName, propertyValue) {
    for (const distributorId of Object.keys(DistributorInfo)) {
        const propertyKey = `${distributorId}_dpn`;
        if (propertyKey &&
            propertyKey in defaultPropertyDefinitions &&
            defaultPropertyDefinitions[propertyKey].label === propertyName) {
            return getSearchUrl(distributorId, propertyValue);
        }
    }
    return undefined;
}
/**
 * Get the designator prefix from properties.
 *
 * @param properties Properties object of any supported shape
 * @returns The designator prefix as a string if found, empty string otherwise
 */
export function getDesignatorPrefixFromProperties(properties) {
    if (!properties)
        return "";
    return (getPropertyValue(defaultPropertyDefinitions.designator_prefix.label, properties) ?? "").toString();
}
/**
 * Get the preferred distributors from properties.
 *
 * @param properties Properties object of any supported shape
 * @returns The preferred distributors as a string if found, empty string otherwise
 */
export function getPreferredDistributorsFromProperties(properties) {
    if (!properties)
        return "";
    return (getPropertyValue(defaultPropertyDefinitions.preferred_distributors.label, properties) ?? "").toString();
}
/**
 * Check if an element should be excluded from the BOM.
 *
 * @param properties Properties object of any supported shape
 * @returns True if the element should be excluded from the BOM, false otherwise
 */
export function getIsExcludedFromBOM(properties) {
    if (!properties)
        return false;
    let value;
    if (areOldProperties(properties)) {
        value = properties[defaultPropertyDefinitions.exclude_from_bom.key];
    }
    else {
        value = getPropertyValue(defaultPropertyDefinitions.exclude_from_bom.label, properties);
    }
    if (!value)
        return false;
    const valueAsString = value.toString();
    return valueAsString.toLowerCase() === "true" || valueAsString === "1";
}
/**
 * Flattens terminal properties into a single properties map.
 *
 * @param terminals Map of terminals
 * @returns Flattened properties map
 */
export function flattenTerminalProperties(terminals) {
    return Object.values(terminals).reduce((acc, val) => {
        return { ...acc, ...val.properties };
    }, {});
}
/**
 * Find default properties that have specific tags.
 *
 * @param tags Tags to search for
 * @returns Array of default properties that have the specified tags
 */
export function findTaggedDefaultProperties(...tags) {
    return Object.values(defaultPropertyDefinitions).filter((property) => {
        if (!property.tags) {
            return false;
        }
        return intersection(tags, property.tags).length > 0;
    });
}
/**
 * Classifies a string value as a boolean.
 *
 * @param value String value to classify
 * @returns True if the value is "true", "on", "yes", or "1", false otherwise
 */
export function booleanClassifier(value) {
    return (value.trim().toLowerCase() === "true" ||
        value.trim().toLowerCase() === "on" ||
        value.trim().toLowerCase() === "yes" ||
        value.trim().toLowerCase() === "1");
}
/**
 * Default properties by label, filtered to only include value-type properties.
 */
const defaultPropertiesByLabel = pickBy(propertyDefinitionsByLabel, (v) => v.type === PropertyTypes.value);
/**
 * The "default value property" is the first defined out of Resistance / Capacitance / Inductance (more generally,
 * the first defined property with {type: PropertyTypes.value} -- which is just those three)
 *
 * This function handles both old and new property shapes.
 *
 * @param properties Properties object of any supported shape
 * @param takeDefaultUnit Whether to include the default unit from the property definition
 * @returns The default value property if found, undefined otherwise
 *
 * @see the initial implementation:
 * https://github.com/buildwithflux/flux-app/blob/32ea44a8cec6d5c0b973265441d57c337a8a2f47/src/storage_engine/helpers/PartStorageHelper.ts#L42
 */
export function getDefaultValueProperty(properties, takeDefaultUnit = false) {
    // Check if the shape of the properties is the old shape
    if (areOldProperties(properties)) {
        // @see https://github.com/buildwithflux/flux-app/pull/14186#discussion_r1921721629
        for (const [key, value] of Object.entries(properties).sort()) {
            const systemDefaultValueProperty = defaultPropertyDefinitions[key];
            if (systemDefaultValueProperty) {
                const unit = takeDefaultUnit ? systemDefaultValueProperty.unit : undefined;
                return {
                    unit,
                    name: systemDefaultValueProperty.label,
                    value,
                };
            }
        }
    }
    else {
        for (const [uid, propertyData] of Object.entries(properties).sort()) {
            const systemDefaultValueProperty = defaultPropertiesByLabel[propertyData.name];
            if (systemDefaultValueProperty) {
                const unit = takeDefaultUnit ? systemDefaultValueProperty.unit : undefined;
                return {
                    uid: uid,
                    unit,
                    ...propertyData,
                };
            }
        }
    }
    return undefined;
}
/**
 * Get the default property from properties.
 *
 * @param properties Properties object of any supported shape
 * @returns The default property if found, undefined otherwise
 */
export function getDefaultProperty(properties) {
    if (!properties)
        return undefined;
    for (const [propertyUid, property] of Object.entries(properties).sort()) {
        const typeProperty = defaultPropertiesByLabel[property.name];
        if (typeProperty) {
            return { uid: propertyUid, ...property };
        }
    }
    return undefined;
}
/**
 * Get the default property value with unit.
 *
 * @param properties Properties object of any supported shape
 * @returns The default property value with unit as a string if found, undefined otherwise
 */
export function getDefaultPropertyValueWithUnit(properties) {
    if (!properties)
        return undefined;
    const property = getDefaultProperty(properties);
    return getValueWithUnit(property);
}
/**
 * Get a property value as a string, with type checking.
 *
 * @param propName Name of the property to retrieve value for
 * @param properties Properties object of any supported shape
 * @returns The property value as a string if found and is a string, undefined otherwise
 */
export function getPropertyValueIfString(propName, properties) {
    if (!properties)
        return undefined;
    const value = getProperty(propName, properties)?.value;
    if (typeof value !== "string") {
        return undefined;
    }
    return value;
}
/**
 * Get a property value as a boolean, with type checking.
 *
 * @param propName Name of the property to retrieve value for
 * @param properties Properties object of any supported shape
 * @returns The property value as a boolean if found and can be converted to a boolean, undefined otherwise
 */
export function getPropertyValueIfBoolean(propName, properties) {
    if (!properties)
        return undefined;
    const value = getProperty(propName, properties)?.value;
    if (typeof value !== "string") {
        return undefined;
    }
    return booleanClassifier(value);
}
/**
 * Get a property value as a number, with type checking.
 *
 * @param propName Name of the property to retrieve value for
 * @param properties Properties object of any supported shape
 * @returns The property value as a number if found and can be converted to a number, undefined otherwise
 */
export function getPropertyValueIfNumber(propName, properties) {
    if (!properties)
        return undefined;
    const value = getProperty(propName, properties)?.value;
    if (value) {
        return Number(value);
    }
    return undefined;
}
/**
 * Get a property value robustly, handling old data in Firebase.
 *
 * @param defaultProperty The default property definition
 * @param properties Properties object of any supported shape
 * @returns The property value if found, undefined otherwise
 */
export function getPropertyValueRobust(defaultProperty, properties) {
    if (!properties)
        return undefined;
    if (areOldProperties(properties)) {
        return properties[defaultProperty.key];
    }
    else {
        return getProperty(defaultProperty.label, properties)?.value;
    }
}
//# sourceMappingURL=PropertyAPI.js.map